import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { recoilLoginState, recoilUserData } from '../RecoilStore';
import './Admin.scss';

// 관리자 화면 공용 아이콘 (1001again-admin.html 시안의 라인 아이콘)
const ICONS: { [key: string]: React.ReactNode } = {
  dashboard: <path d="M4 13h7V4H4v9zm0 7h7v-5H4v5zm9 0h7v-9h-7v9zm0-16v5h7V4h-7z"/>,
  info: <path d="M4 5h16v14H4zM4 9h16M9 9v10"/>,
  gallery: <><path d="M3 5h18v14H3zM3 16l5-5 4 4 3-3 6 6"/><circle cx="8.5" cy="9.5" r="1.5"/></>,
  support: <path d="M12 20s-7-4.4-7-9a4 4 0 017-2.6A4 4 0 0119 11c0 4.6-7 9-7 9z"/>,
  popup: <path d="M3 5h18v12H3zM8 21h8M12 17v4"/>,
  backup: <path d="M4 7h16v13H4zM4 7l2-3h12l2 3M9 12h6"/>,
  external: <path d="M14 5h5v5M19 5l-8 8M18 14v5H5V6h5"/>,
  logout: <path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10"/>,
  menu: <path d="M4 7h16M4 12h16M4 17h16"/>,
  chevron: <path d="M9 6l6 6-6 6"/>,
  plus: <path d="M12 5v14M5 12h14"/>,
  minus: <path d="M5 12h14"/>,
  edit: <path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4"/>,
  trash: <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6"/>,
  up: <path d="M12 19V5M6 11l6-6 6 6"/>,
  down: <path d="M12 5v14M6 13l6 6 6-6"/>,
  image: <><path d="M3 5h18v14H3zM3 16l5-5 4 4 3-3 6 6"/><circle cx="8.5" cy="9.5" r="1.5"/></>,
  save: <path d="M12 3v12M7 10l5 5 5-5M5 21h14"/>,
  close: <path d="M6 6l12 12M18 6L6 18"/>,
  leaf: <path d="M12 21V11M12 11c0-4 3-7 7-7 0 4-3 7-7 7zM12 15c0-3.3-2.7-6-6-6 0 3.3 2.7 6 6 6z"/>,
};

export function Icon({ name }: { name: string }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS[name]}</svg>;
}

// 페이지 상단 제목 영역
export function PageHead({ title, desc, children }: { title: string; desc?: string; children?: React.ReactNode }) {
  return (
    <div className="ad-head">
      <div>
        <h1>{title}</h1>
        {desc && <p>{desc}</p>}
      </div>
      {children && <div className="ad-acts">{children}</div>}
    </div>
  );
}

export const ADMIN_MENUS = [
  { key: 'main', title: '대시보드', url: '/admin/main', icon: 'dashboard' },
  { key: 'main-info', title: '사이트 정보 편집', url: '/admin/edit/main-info', icon: 'info', desc: '인사말 · 기관 소개 · 서비스 · 시설 · 오시는 길 · 메인 배경' },
  { key: 'gallery', title: '갤러리 관리', url: '/admin/edit/gallery', icon: 'gallery', desc: '프로그램 · 후원물품 사진' },
  { key: 'support', title: '후원 · 실습 관리', url: '/admin/edit/support-practice', icon: 'support', desc: '후원 · 자원봉사 · 실습 안내' },
  { key: 'popup', title: '팝업 관리', url: '/admin/edit/popup', icon: 'popup', desc: '첫 화면 팝업 · 게시 기간' },
];
const SYSTEM_MENUS = [
  { key: 'backup', title: '백업', url: '/admin/backup', icon: 'backup' },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const setIsLogin = useSetRecoilState(recoilLoginState);
  const userData = useRecoilValue(recoilUserData);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const current = [...ADMIN_MENUS, ...SYSTEM_MENUS].find((m) => location.pathname.startsWith(m.url));
  const userName = userData?.userName || '관리자';

  const logout = () => {
    if (!window.confirm('로그아웃 하시겠습니까?')) return;
    setIsLogin(false);
    navigate('/admin');
  };

  const renderItem = (m: { key: string; title: string; url: string; icon: string }) => (
    <NavLink key={m.key} to={m.url} className={({ isActive }) => `ad-side__item ${isActive ? 'is-on' : ''}`}>
      <Icon name={m.icon} />
      {m.title}
    </NavLink>
  );

  return (
    <div className="ad-app">
      <aside className={`ad-side ${drawerOpen ? 'is-open' : ''}`}>
        <div className="ad-side__brand">
          <span className="mark"><Icon name="leaf" /></span>
          <span>
            <b>새삶공동체</b>
            <i>ADMIN CONSOLE</i>
          </span>
        </div>

        <nav className="ad-side__nav" aria-label="관리자 메뉴">
          <div className="ad-side__group">
            <p>관리</p>
            {ADMIN_MENUS.map(renderItem)}
          </div>
          <div className="ad-side__group">
            <p>시스템</p>
            {SYSTEM_MENUS.map(renderItem)}
          </div>
        </nav>

        <div className="ad-side__foot">
          <a href="/" target="_blank" rel="noopener noreferrer">
            <Icon name="external" />
            사이트 보기
          </a>
          <button type="button" onClick={logout}>
            <Icon name="logout" />
            로그아웃
          </button>
        </div>
      </aside>
      <div className={`ad-scrim ${drawerOpen ? 'is-on' : ''}`} onClick={() => setDrawerOpen(false)} />

      <div className="ad-main">
        <header className="ad-top">
          <button type="button" className="ad-top__burger" aria-label="메뉴 열기" aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(!drawerOpen)}>
            <Icon name="menu" />
          </button>
          <p className="ad-crumb">
            관리자
            <Icon name="chevron" />
            <b>{current ? current.title : '대시보드'}</b>
          </p>
          <div className="ad-top__right">
            <span className="ad-who"><span>{userName.slice(0, 1)}</span><b>{userName}</b></span>
          </div>
        </header>

        <div className="ad-page">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
